import type { HistoryEntry, UndoOutcome } from './types';
import { STORAGE_KEY, ARCHIVED_KEY, MERGED_KEY, APPROVED_CAT_KEY, MOCK_UNDONE_KEY } from './constants';
import { MOCK } from './mockData';

export function addCleanupHistoryEntry(entry: HistoryEntry): void {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const stored: HistoryEntry[] = raw ? JSON.parse(raw) : [];
    const updated = [entry, ...stored];
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {}
}

export function removeCleanupHistoryEntry(id: string): void {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const stored: HistoryEntry[] = raw ? JSON.parse(raw) : [];
    const updated = stored.filter((e) => e.id !== id);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {}
}

export function loadUndoneMockIds(): Set<string> {
  try {
    const raw = window.localStorage.getItem(MOCK_UNDONE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function markMockUndone(id: string): void {
  try {
    const current = loadUndoneMockIds();
    current.add(id);
    window.localStorage.setItem(MOCK_UNDONE_KEY, JSON.stringify(Array.from(current)));
  } catch {}
}

function removeIdsFromStorageSet(key: string, idsToRemove: string[]): void {
  try {
    const raw = window.localStorage.getItem(key);
    const current: string[] = raw ? JSON.parse(raw) : [];
    const removeSet = new Set(idsToRemove);
    const updated = current.filter((id) => !removeSet.has(id));
    window.localStorage.setItem(key, JSON.stringify(updated));
  } catch {}
}

function buildUndoMessage(entry: HistoryEntry): string {
  if (entry.job === 'archive') {
    const count = entry.undoPayload?.kind === 'archive' ? entry.undoPayload.accountIds.length : 1;
    return `Archive undone. ${count} account${count !== 1 ? 's' : ''} restored.`;
  }
  if (entry.job === 'merge') {
    const count = entry.undoPayload?.kind === 'merge' ? entry.undoPayload.pairIds.length : 1;
    return `Merge undone. ${count} pair${count !== 1 ? 's' : ''} restored.`;
  }
  if (entry.job === 'categorize') {
    const count = entry.undoPayload?.kind === 'categorize' ? entry.undoPayload.transactionIds.length : 1;
    return `Categorize undone. ${count} transaction${count !== 1 ? 's' : ''} back to Uncategorized.`;
  }
  return 'Entry removed from history.';
}

export function undoHistoryEntry(id: string): UndoOutcome {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const stored: HistoryEntry[] = raw ? JSON.parse(raw) : [];
    const storedEntry = stored.find((e) => e.id === id);
    const mockEntry = MOCK.find((e) => e.id === id);
    const target = storedEntry ?? mockEntry;
    if (!target) {
      return { success: false, message: 'Could not find that history entry.' };
    }

    if (target.undoPayload) {
      const payload = target.undoPayload;
      if (payload.kind === 'archive') {
        removeIdsFromStorageSet(ARCHIVED_KEY, payload.accountIds);
      } else if (payload.kind === 'merge') {
        removeIdsFromStorageSet(MERGED_KEY, payload.pairIds);
      } else if (payload.kind === 'categorize') {
        removeIdsFromStorageSet(APPROVED_CAT_KEY, payload.transactionIds);
      }
    }

    if (storedEntry) {
      const updated = stored.filter((e) => e.id !== id);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    }
    if (mockEntry) {
      markMockUndone(id);
    }

    return { success: true, message: buildUndoMessage(target) };
  } catch {
    return { success: false, message: 'Could not undo this action.' };
  }
}